import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataIndexMenuTableContainer } from '../DataManager.style';
import { Modal, Progress, ProgressProps, Select, Spin, Tabs, Tooltip, Upload, UploadProps, message } from 'antd';
import { ReactComponent as ExpandIcon } from "../../../assets/svgs/icons/expand-icon.svg";
import { ReactComponent as CloseIcon } from "../../../assets/svgs/icons/close-icon.svg";
import { ReactComponent as ShrinkIcon } from "../../../assets/svgs/icons/shrink-icon.svg";
import { ReactComponent as StopIcon } from "../../../assets/svgs/icons/stop-icon.svg";
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { FileUploadItem, UploadCompleteEvent } from '../../../types/interfaces';
import { emitCustomEvent } from 'react-custom-events';
import { ProgressContainer } from '../../tables/dataIndexTable/uploadsTable/upload-table.styles';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';

import axios from "axios";
// import { CloseCircleOutlined } from "@ant-design/icons";
// import { UploadContainer } from "./upload-container.styles";
import { getHash } from '../../../common/uploadUtils';
import { completeMultipartUpload, getUploadableOwners, handleMultipartUpload, onSelectOwnerIdModalCancel, postHashFile, preHashFile } from './UploadLambdaUtils';
import { checkUploadConditions } from './UploadUtils';
import { CloseOutlined } from '@ant-design/icons';

const { Dragger } = Upload;

interface UploadsMenuTableProps {
    visible: boolean;
    setActiveMenu: (menuName: string | null) => void;
}


interface OwnerIdvalue {
    value: string;
    label: string;
}


const UploadsMenuTable = ({ visible, setActiveMenu }: UploadsMenuTableProps) => {
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [fileDataArray, setFileDataArray] = useState<FileUploadItem[]>([]);
    const [expand, setExpand] = useState<boolean>(false);
    const [selectedOwnerId, setSelectedOwnerId] = useState<string>("");
    const [ownerIds, setOwnerIds] = useState<OwnerIdvalue[]>([]);
    const [isHashing, setIsHashing] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const selectRef = useRef<any>(null);
    const fileArrayHash = () => {
        setOpenConfirmModal(false);
        setIsHashing(true);
        setDataLoading(true);
        setIsUploading(true);
        return new Promise(async (resolve, reject) => {
            let hasCount = 0;

            for (let i = 0; i < fileDataArray.length; i++) {
                const isHash = fileDataArray[i].hash
                if (isHash) {
                    hasCount++;
                    if (hasCount === fileDataArray.length) {
                        resolve(true);
                        setFileDataArray(fileDataArray);
                        setIsHashing(false);
                        setDataLoading(true);

                    }
                    continue;
                }

                const file = fileDataArray[i].file;

                let data:any;
                try {
                    data = await preHashFile(file);
                }catch(err) {
                    message.error(JSON.stringify(err || "Error hashing file."));
                    reject(err);
                    setIsHashing(false);
                    setDataLoading(true);
                    return;
                }

                const body = JSON.parse(data);
                if (!body.permission) {
                    message.error(body.error_message);
                    reject(body.error_message);
                    setIsHashing(false);
                    setDataLoading(true);
                    return
                }
                const ownerIdList: string[] = body.owner_ids;

                if (!ownerIdList || ownerIdList.length === 0) {
                    message.warning("There is no an ownerId.", 2);
                    reject("There is no an ownerId.");
                    setIsHashing(false);
                    setDataLoading(true);
                    return;
                }
                const hash = await getHash(file);
                // setFileHash(hash);
                setOwnerIds(ownerIdList.map((ownerId) => ({ value: ownerId, label: ownerId })));
                
                fileDataArray[i].hash = hash;
                hasCount++;
                if (hasCount === fileDataArray.length) {
                    resolve(true);
                    setFileDataArray(fileDataArray);
                    setIsHashing(false);
                    setDataLoading(true);
                }
            }
        })
    };
    // get the CancelToken object from axios
    const CancelToken = axios.CancelToken;
    // create a cancel token instance
    const cancelTokenSource = CancelToken.source();
    const [newFiles, setNewFiles] = useState<FileUploadItem[]>([]);
    useEffect( () => {
        setNewFiles(fileDataArray.filter((file: FileUploadItem) => file.hash === ""))
        if(isHashing) {
            return;
        }
        setDataLoading(false);
        if(fileDataArray.length === 0) {
            return;
        }

        async function upload() {
            for (let i = 0; i < fileDataArray.length; i++) {
                const file = fileDataArray[i];
                if (!file.hash) {
                    continue;
                }
                if (file.status === "success") {
                    continue;
                }
                if (file.status === "active") {
                    break;
                }
                if (file.status === "exception" || file.status === "cancelled") {
                    continue;
                }
                file.status = "active";

                const cancelUpload = () => {
                    file.status = "cancelled";
                    file.uploadProgress = 0;
                    setFileDataArray([...fileDataArray]);
                    onSelectOwnerIdModalCancel(file.hash, file.ownerId).then((res) => {
                        message.success("Upload canceled.");
                    }).catch((err) => {
                        message.error(err);
                    });
                }

                await postHashFile(file.file, file.hash, file.ownerId).then(async (res: any) => {
                    const data = JSON.parse(res);
                    if (data.errorMessage) {
                        message.error(data.errorMessage);
                        file.status = "exception";
                        setFileDataArray([...fileDataArray]);
                        return;
                    }
                    if (data.type === "multi_part") {
                        const etags = await handleMultipartUpload(data, file.file, (percentage) => {
                            file.uploadProgress = percentage;
                            setFileDataArray([...fileDataArray]);
                        }, file );

                        if(etags === null) {
                            cancelUpload()
                            return;
                        }

                        completeMultipartUpload(file, etags).then((res : any) => {
                            const data = JSON.parse(res);

                            if (Object.hasOwn(data, "errorMessage")) {
                                message.error(data.errorMessage || data.body);
                                file.status = "exception";
                                setFileDataArray([...fileDataArray]);
                            } else {
                                file.status = "success";
                                emitCustomEvent("UploadComplete", { success: true, fileData: file } as UploadCompleteEvent)
                                setFileDataArray([...fileDataArray]);

                                // emitCustomEvent("UploadedData", { success: true, data_id: fileHash });
                                // resetValues();
                            }
                        }).catch((err) => {
                            message.error(err);
                            file.status = "exception";
                            setFileDataArray([...fileDataArray]);
                        });


                        // fileArrayUpload();
                    } else if (data.signed_url) {
                        const signedUrl = data.signed_url;
                        const controller = new AbortController();
                        file.cancelCallback = () => {
                            controller.abort();
                        }
                        axios
                            .put(signedUrl, file.file, {
                                headers: { "Content-Type": "application/pointcloud" },
                                cancelToken: cancelTokenSource.token,
                                signal: controller.signal,
                                onUploadProgress: (e: any) => {
                                    const percentCompleted = Math.round((e.loaded * 100) / e.total);
                                    file.uploadProgress = percentCompleted;
                                    setFileDataArray([...fileDataArray]);
                                    // emitCustomEvent("UploadingData", { percent: percentCompleted, data_id: hash });
                                }
                            })
                            .then(() => {
                                file.status = "success";
                                emitCustomEvent("UploadComplete", { success: true, fileData: file } as UploadCompleteEvent)
                                setFileDataArray([...fileDataArray]);
                            })
                            .catch((e) => {
                                if(axios.isCancel(e)) {
                                    cancelUpload();
                                    return;
                                }
                                message.error(e);
                                file.status = "exception";
                                setFileDataArray([...fileDataArray]);
                            });
                    } else {
                        message.warning(data.error_message, 2);
                        file.status = "exception";
                        setFileDataArray([...fileDataArray]);
                    }
                }).catch((err) => {
                    message.error(err);
                    file.status = "exception";
                    setFileDataArray([...fileDataArray]);
                });

            }
        }
        upload();
        
    }, [isHashing, fileDataArray]);

    useEffect(() => {
        const allFilesProcessed = fileDataArray.every(file => file.status === "success" || file.status === "exception");
        setIsUploading(!allFilesProcessed);
    }, [fileDataArray]);

    const props: UploadProps = {
        name: 'file',
        multiple: true,
        customRequest: () => null,
        onDrop(event) {
            if (isUploading) {
                event.preventDefault();
                event.stopPropagation();
                message.warning("Uploads are in progress. Please wait.");
                return;
            }

            if (!selectedOwnerId) {
                message.warning("Please select an owner ID.");
                return;
            }
            const filesArray = Array.from(event.dataTransfer.files);
            let validFiles  = filesArray.filter((file : File) => checkUploadConditions(file));
            let validFileObjects: FileUploadItem[] = validFiles.map((file: File) => {
                return {
                    file: file,
                    hash: "",
                    ownerId: selectedOwnerId,
                    status: "pending",
                    name: file.name,
                    cancelCallback: () => {}
                };
            });

            setFileDataArray((prevArray: FileUploadItem[]) => {
                const newArray = [...prevArray, ...validFileObjects];
                return newArray;
            });
            setOpenConfirmModal(true);
        },
        onChange(info) {
            if (isUploading) {
                message.warning("Uploads are in progress. Please wait.");
                return;
            }

            const file = info.file.originFileObj;
            const files = fileDataArray.map((file: FileUploadItem) => file.file);

            if(!file) return;

            if (files.includes(file)) {
                return;
            }

            // Check valid file
            if (!checkUploadConditions(file)) {
                return;
            }
            if (!selectedOwnerId) {
                message.warning("Please select an owner ID.");
                return;
            }
            const fileUpload: FileUploadItem = {
                file: file,
                hash: "",
                ownerId: selectedOwnerId,
                status: "pending",
                name: file.name,
                cancelCallback: () => {}
            };
            setFileDataArray((prevArray: FileUploadItem[]) => {
                const newArray = [...prevArray, fileUpload];
                return newArray;
            });
            setOpenConfirmModal(true);
        },
        showUploadList: false,
        disabled: isUploading,
    };

    const columnsData = [
        { title: "", key: "show", size: 35 },
        { title: "Name", key: "name", size: 250, },
        { title: "Owner", key: "ownerId", size: 150 },
        { title: "Status", key: "status", size: 150 },
    ];
    const [cancelItem, setCancelItem] = useState<FileUploadItem | null>(null);
    const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
        if (fileDataArray.length === 0) {
            return [];
        }
        return columnsData.map(({ title, key, size }) => {
            if (key === "show") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: title || "",
                    size: size,
                    Cell: ({ row }) => {
                        const record: FileUploadItem = row.original;

                        let progressStatus: ProgressProps["status"] = record.status as ProgressProps["status"];
                        if (record.status === "cancelled") {
                            return (
                                <ProgressContainer>
                                    <Tooltip title="Upload Cancelled">
                                        <CloseOutlined className="upload_cancel_icon" style={{ color: "red" }} />
                                    </Tooltip>
                                </ProgressContainer>
                            );
                        }

                        return (
                            <ProgressContainer>
                                <Progress
                                    className="upload-progress upload_progress_data"
                                    type="circle"
                                    percent={record.uploadProgress}
                                    size="small"
                                    status={progressStatus}
                                />
                            </ProgressContainer>
                        );
                    }
                }
            } else if (key === "name") {
                return {
                    accessorKey: key,
                    header: title || "",
                    size: size,
                    Cell: ({ row }) => {
                        const record: FileUploadItem = row.original;
                        const onClick = () => {
                            if (record.status === "success") {
                                return;
                            }
                            if (record.status === "exception" || record.status === "cancelled") {
                                return;
                            }
                            setCancelItem(record);
                        }
                        return (
                            <div className='upload_name_box'>
                                {record.name}
                                {
                                     record.status === "pending" || record.status === "active" ? (
                                        <Tooltip placement="bottom" title="Cancel" >
                                            <StopIcon key="StopIcon" className="upload_stop_icon" onClick={onClick} />
                                        </Tooltip>
                                    ) : null
                                }

                            </div>
                        );
                    }
                }

            } else if (key === "status") {
                return {
                    accessorKey: key,
                    header: title || "",
                    size: size,
                    Cell: ({ row }) => {
                        const record: FileUploadItem = row.original;
                        return (
                            <>
                                {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                            </>
                        );
                    }
                }
            } else {
                let data = {
                    accessorKey: key,
                    header: title || "",
                    size: size,
                }
                return data
            };

        })
    }, [fileDataArray]);

    const table = useMaterialReactTable({
        columns,
        data: fileDataArray,
        enableSorting: false,
        positionToolbarAlertBanner: 'none',
        rowNumberDisplayMode: "static",
        enablePagination: false,
        enableBottomToolbar: false,
        initialState: {
            showGlobalFilter: true,
            // pagination: {
            //     pageSize: 10,
            //     pageIndex: 0
            // },
        },
    });

    if (!table) {
        return null;
    }
    const tabItems = [
        {
            label: (
                <span>
                    Uploads
                </span>
            ),
            key: '1',
            children: (
                <div className="inputData_table_searchable dataIndex_table upload_data_container">
                    <Select
                        style={{ width: 150, marginBottom: 10}}
                        defaultValue={ownerIds[0]?.value}
                        options={ownerIds}
                        ref={selectRef}
                        onChange={(value) => setSelectedOwnerId(value)}
                        className="custom-select-dropdown"
                        value={selectedOwnerId}
                    />
                    <Dragger {...props}>
                        <p className="ant-upload-drag-icon">
                            <SimCardDownloadOutlinedIcon style={{ color: '#36BDBE' }} />
                        </p>
                        <p className="ant-upload-text">Drag & Drop file here or Choose File <span className='fileText'>Choose File</span></p>
                    </Dragger>
                    <div className='loading_box'>
                        <Spin spinning={dataLoading}>
                            {fileDataArray && fileDataArray.length > 0 && (
                                <div className={`materialReactTableCustom aois_table uploadMenuTable ${expand ? 'expand' : ''}`}>
                                    <MaterialReactTable table={table} />
                                </div>
                            )}
                        </Spin>
                    </div>
                </div>
            ),
        },
    ];

    function closeCardBox(value: any) {
        setActiveMenu(null)
    }

    function expandTable(value: any) {
        setExpand(!expand)
    }
    const onConfirmModalCancel = () => {
        setOpenConfirmModal(false);
        setFileDataArray(fileDataArray.filter((file: FileUploadItem) => file.hash));
    };
    useEffect(() => {
        setSelectedOwnerId(ownerIds[0]?.value);
    },[ownerIds]);

    useEffect(() => {
        setDataLoading(true);
        getUploadableOwners().then((res: any) => {
            const data = JSON.parse(res);
            const uploadable = data.all_uploadable;

            if(!uploadable) {
                message.error("You don't have permission to upload files.");
                return;
            }
            if(!uploadable?.length ){
                message.error("There is no an ownerId.");
                return;
            }
            setOwnerIds(uploadable.map((ownerId : any) => ({ value: ownerId, label: ownerId })));
        }).catch((err) => {
            message.error(err);
        }).finally(() => {
            setDataLoading(false);
        });
    },[]);

    const cancelUpload = () => {
        if (!cancelItem) return;
        cancelItem.cancelCallback();
        setCancelItem(null);
    }

    return (
        <>
        {
            visible &&
            <>
                <DataIndexMenuTableContainer>
                    <div className='table-container'>
                        <div className="custom-tab-container">
                            <Tabs defaultActiveKey="1"
                                items={tabItems}
                                key="1"
                                className="custom-tabs">
                            </Tabs>
                            <div className="tab-icons">
                                    {/* <ReloadOutlined key="reload" title="Reload" style={{ color : "#ffffff"}} /> */}
                                {expand ?
                                    <ShrinkIcon key="ShrinkIcon" title="Shrink" color='#ffffff' onClick={expandTable} style={{ marginLeft: 8 }}/>
                                    :
                                    <ExpandIcon key="ExpandIcon" title="Expand" color='#ffffff' onClick={expandTable} style={{ marginLeft: 8 }}/>
                                }
                                <CloseIcon key="CloseIcon" title="Close" color='#ffffff' style={{ marginLeft: 8 }} onClick={closeCardBox}/>
                            </div>
                        </div>
                    </div>
                </DataIndexMenuTableContainer>
            </>
        }
            <Modal title="Confirm" open={openConfirmModal} onOk={fileArrayHash} onCancel={onConfirmModalCancel}>
                <p>
                    Are you sure you want to upload{" "}
                    {newFiles.length > 1 ? newFiles.length + " files" : newFiles[0]?.file.name}?
                </p>
                {/* <div style={{display: "flex", gap: "10px", paddingTop: "10px", alignItems: "center"}}>
                    <label>Owner ID</label>
                    <Select
                        style={{ width: 270 }}
                        defaultValue={selectedOwnerId}
                        options={ownerIds}
                        ref={selectRef}
                        onChange={(value) => setSelectedOwnerId(value)}
                    />
                </div> */}
                
            </Modal>

            <Modal title="Cancel" open={Boolean(cancelItem)} onOk={cancelUpload} onCancel={() => {
                setCancelItem(null);
            }}>
                <p>
                    Are you sure you want to cancel uploading {" "}
                    {cancelItem?.name}?
                </p>
            </Modal>

        </>
    );
}

export default UploadsMenuTable;
